import { Lightning, Utils } from "@lightningjs/sdk";
import { Card } from "./Card";
import { FOCUS_TRANSITION } from "./focus";
/** Align cap-height text from the design with Lightning's baseline. */
export function planTextInset(fontFace, fontSize) {
    return fontSize * (fontFace === "Insatiable Display" ? 0.26 : 0.2890625);
}
export function planText(fontFace, fontSize, text) {
    return {
        text,
        fontFace,
        fontStyle: fontFace === "Insatiable Display" ? "700" : "400",
        fontSize,
        lineHeight: fontSize * 1.2,
        offsetY: fontSize,
        textColor: 0xffffffff,
        precision: 1,
    };
}
export class PlanCard extends Lightning.Component {
    constructor() {
        super(...arguments);
        this._expansion = 0;
    }
    static _template() {
        return {
            w: this.W,
            h: this.EXPANDED_H,
            Background: {
                w: this.W,
                h: this.EXPANDED_H,
                rect: true,
                color: 0xff161616,
                clipping: true,
                renderToTexture: true,
                shader: { type: Lightning.shaders.RoundedRectangle, radius: 30 },
                Pattern: {
                    x: -155,
                    w: 710,
                    h: 597,
                    alpha: 0.5,
                    src: Utils.asset("images/subscriptions/ligue-1-pattern.png"),
                },
                Ligue3: {
                    visible: false,
                    w: 436,
                    h: 550,
                    pivotY: 0,
                    Pattern: { w: 436, h: 550 },
                    Decoration: {
                        w: 401.334,
                        h: 550,
                        src: Utils.asset("images/subscriptions/ligue-3-card-decoration.png"),
                    },
                    Waves: {
                        x: 20.486,
                        y: 240.88,
                        w: 415.514,
                        h: 309.12,
                        src: Utils.asset("images/subscriptions/ligue-3-card-waves.png"),
                    },
                },
            },
            Badge: {
                x: 246,
                w: 190,
                h: 44,
                visible: false,
                rect: true,
                color: 0xfffe80e0,
                shader: {
                    type: Lightning.shaders.RoundedRectangle,
                    radius: [4, 30, 4, 4],
                },
                Label: {
                    y: 6,
                    text: Object.assign(Object.assign({}, planText("Roboto Condensed", 24, "Most Popular")), { w: 190, textAlign: "center", textColor: 0xff1e0046 }),
                },
            },
            Title: {
                x: 28,
                y: 55,
                text: Object.assign(Object.assign({}, planText("Insatiable Display", 46, "")), { w: 380 }),
            },
            Subtitle: {
                x: 28,
                y: 114,
                text: Object.assign(Object.assign({}, planText("Roboto Condensed", 24, "")), { w: 380 }),
            },
            Features: { x: 27, y: 176 },
            Price: {
                x: 403,
                y: 500,
                mountX: 1,
                flex: { direction: "row", alignItems: "flex-start" },
                Amount: { text: Object.assign(Object.assign({}, planText("Insatiable Display", 55, "")), { h: 66 }) },
                Suffix: {
                    flexItem: { marginTop: 18 },
                    text: Object.assign(Object.assign({}, planText("Insatiable Display", 37, "")), { h: 44.4 }),
                },
            },
            Outline: {
                alpha: 0.5,
                color: Card.BORDER_COLOR,
                texture: Card.borderTexture(this.W, this.EXPANDED_H, 30, 1),
            },
            Border: {
                alpha: 0,
                color: Card.BORDER_COLOR,
                texture: Card.borderTexture(this.W, this.EXPANDED_H, 30, 4),
            },
        };
    }
    set item(item) {
        const fontSize = item.featureFontSize;
        const ligue3 = !!item.artwork;
        const compact = item.artwork === "ligue-3-compact";
        let featureY = 0;
        this.patch({
            Background: {
                Pattern: { visible: !ligue3 },
                Ligue3: {
                    visible: ligue3,
                    // Exports include the design's colour blending and layer opacity.
                    Pattern: {
                        src: ligue3
                            ? Utils.asset(`images/subscriptions/${item.artwork}-pattern.png`)
                            : undefined,
                    },
                    Decoration: { visible: compact },
                    Waves: { visible: compact },
                },
            },
            Badge: {
                visible: !!item.popular,
                color: ligue3 ? 0xffd8c9a7 : 0xfffe80e0,
            },
            Title: { text: { text: item.title.toUpperCase() } },
            Subtitle: { text: { text: item.subtitle } },
            Price: {
                Amount: { text: { text: item.price } },
                Suffix: { text: { text: item.priceSuffix.toUpperCase() } },
            },
            Features: {
                children: item.features.map((text) => {
                    const y = featureY;
                    const lines = text.split("\n").length;
                    featureY += fontSize * 0.7109375 + (lines - 1) * fontSize * 1.2 + 24;
                    return {
                        y,
                        w: 377,
                        Dot: {
                            y: 1,
                            w: 15,
                            h: 15,
                            texture: Lightning.Tools.getSvgTexture(Utils.asset(`images/subscriptions/${ligue3 ? "ligue-3" : "ligue-1"}-dot.svg`), 30, 30),
                        },
                        Label: {
                            x: 24,
                            y: -planTextInset("Roboto Condensed", fontSize),
                            text: Object.assign(Object.assign({}, planText("Roboto Condensed", fontSize, text)), { h: lines * fontSize * 1.2, w: 353, wordWrap: false }),
                        },
                    };
                }),
            },
        });
    }
    set expansion(value) {
        this._expansion = value;
        const height = PlanCard.H + (PlanCard.EXPANDED_H - PlanCard.H) * value;
        const inset = (PlanCard.EXPANDED_H - height) / 2;
        this.patch({
            y: inset,
            Background: {
                h: height,
                Ligue3: { scaleY: height / 550 },
                Pattern: { x: -217 + 62 * value, w: 654 + 56 * value, h: height },
            },
            Title: { y: 29 + 26 * value },
            Subtitle: { y: 87 + 27 * value },
            Features: { y: 165 + 15 * value, scale: 0.92 + 0.08 * value },
            Price: { y: 439 + 57 * value },
            Outline: { h: height },
            Border: { h: height },
        });
    }
    get expansion() {
        return this._expansion;
    }
    setExpanded(expanded) {
        this.setSmooth("expansion", expanded ? 1 : 0, PlanCard.EXPANSION_TRANSITION);
    }
    _focus() {
        this.tag("Border").setSmooth("alpha", 1, FOCUS_TRANSITION);
    }
    _unfocus() {
        this.tag("Border").setSmooth("alpha", 0, FOCUS_TRANSITION);
    }
}
PlanCard.W = 436;
PlanCard.H = 550;
PlanCard.EXPANDED_W = 436;
PlanCard.EXPANDED_H = 597;
PlanCard.EXPANSION_TRANSITION = {
    duration: 0.25,
    timingFunction: "cubic-bezier(0.2, 0, 0, 1)",
};
//# sourceMappingURL=PlanCard.js.map